import { api } from '../api';

/**
 * Resultado de la consulta pública de Medicard por DNI. El server (proxy
 * `@Public` bajo `/credencial`) devuelve el número + nombre + plan (MS1/MS2);
 * el front confirma la identidad ("¿Sos NOMBRE?") antes de revelar el número.
 * NO expone PIN ni fecha de nacimiento. `plan` es best-effort (null si no se
 * resuelve contra Binary).
 *  - `multiple`: el DNI matchea más de un afiliado y no podemos elegir uno.
 *  - `no-encontrado`: ningún afiliado con ese DNI.
 */
/** Un afiliado para elegir cuando el DNI está compartido (sin Medicard: se
 *  obtiene al elegir con `consultarMedicardAfiliado`). */
export interface AfiliadoSeleccion {
  id: string;
  nombre: string;
  fechaNacimiento: string | null;
}

export type ConsultaMedicard =
  | { encontrado: true; medicard: string; nombre: string; plan: 'MS1' | 'MS2' | 'otro' | null }
  | { encontrado: false; motivo: 'no-encontrado' }
  | { encontrado: false; motivo: 'multiple'; afiliados?: AfiliadoSeleccion[] };

/**
 * `GET /credencial/medicard?doc=<DNI>`. Si el DNI está compartido devuelve
 * `{ encontrado:false, motivo:'multiple', afiliados:[...] }` para que el socio
 * elija. Lanza ante error de red/HTTP (429/503) — la página lo traduce.
 */
export async function consultarMedicard(doc: string): Promise<ConsultaMedicard> {
  const { data } = await api.get<ConsultaMedicard>('/credencial/medicard', {
    params: { doc },
  });
  return data;
}

/** Medicard del afiliado elegido (DNI compartido): `?doc=&afiId=`. */
export async function consultarMedicardAfiliado(doc: string, afiId: string): Promise<ConsultaMedicard> {
  const { data } = await api.get<ConsultaMedicard>('/credencial/medicard', {
    params: { doc, afiId },
  });
  return data;
}

/**
 * Credencial digital temporal por DNI. El server devuelve el número de Medicard,
 * el nombre y el token rotativo de 4 dígitos (`token`) con los segundos que le
 * quedan de vigencia (`time`, máx. 300). El front arma la tarjeta + el QR
 * (`medicard|token|0,00000|0,00000|0`) y re-consulta al expirar el token.
 */
export type CredencialTemporalData =
  | { encontrado: true; medicard: string; nombre: string; token: string; time: number }
  | { encontrado: false; motivo: 'no-encontrado' };

/**
 * `GET /credencial/temporal?doc=<DNI>`. Lanza ante error de red/HTTP (429 de
 * rate-limit, 503 si Oracle no responde/está deshabilitado) — la página lo
 * traduce al mensaje correspondiente.
 */
export async function consultarCredencialTemporal(doc: string): Promise<CredencialTemporalData> {
  const { data } = await api.get<CredencialTemporalData>('/credencial/temporal', {
    params: { doc },
  });
  return data;
}
